import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';
import { ProductData } from 'types/ProductData';
import { WholesaleGateModal } from 'ui';
import { ProductGallery } from 'ui/Carousel/ProductGallery';
import Banner from 'public/components/DefaultLayout/Banner';
import {
  CartActionType,
  CartProduct,
  useCartDispatch,
  useCartState,
} from 'public/context/CartContext';
import { useBannerState, useCategoryState } from 'public/context';
import { publicPath } from 'enums/publicRoutes';
import { useAuth } from 'hooks/useAuth';
import useMarketType from 'utils/useMarketType';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useIsScrolledPast } from 'utils/useIsScrolledPast';
import { GlobalStyle, StyledProductView } from './style';
import ProductBreadcrumbs from './components/ProductBreadcrumbs';
import ProductHero from './components/ProductHero';
import ProductTabs from './components/ProductTabs';
import FarmLocationCard from './components/FarmLocationCard';
import FarmProducts from './components/FarmProducts';
import StickyAddToCart from './StickyAddToCart';
import { buildProductBreadcrumbs } from './utils/productBreadcrumbs';

export interface ProductViewProps {
  product?: ProductData;
}

const ProductView = ({ product }: ProductViewProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { isEmarket, marketType } = useMarketType();
  const { user } = useAuth();
  const { banners } = useBannerState();
  const { category } = useCategoryState();
  const { farmProducts } = useCartState();
  const cartDispatch = useCartDispatch();

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isGateModalOpen, setIsGateModalOpen] = useState(false);

  const addToCartRef = useRef<HTMLDivElement>(null);

  const productAmount = product?.product_amounts?.find((amount) =>
    isEmarket ? amount.type === 'EMARKET' : amount.type !== 'EMARKET'
  );

  const qualityMarks = product?.qualitymarks?.filter(
    (qmark, index, all) =>
      all.findIndex((x) => x.quality_mark_id === qmark.quality_mark_id) ===
      index
  );

  const breadcrumbs = useMemo(
    () => buildProductBreadcrumbs(product, category, isEmarket),
    [product, category, isEmarket]
  );
  const backTo = breadcrumbs[breadcrumbs.length - 1];

  const { data: otherProducts, appendData: fetchFarmProducts } =
    useQueryApiClient({
      request: {
        url: `api/v2/farms/${product?.farm?.id}/products`,
        disableOnMount: !product?.farm?.id,
        data: { page: 1, marketType },
      },
    });

  const matchedCartProduct: CartProduct | undefined = farmProducts
    .flatMap((farm) => farm.products)
    .find((cartProduct) => cartProduct?.cartProduct?.id === product?.id);
  const amountCart = matchedCartProduct ? matchedCartProduct.amount : 0;

  const hasWholesaleAccess =
    !!user?.authenticated && !!user?.farm?.has_wholesale_contract;

  const productHasDiscount = !!(
    productAmount?.storage_stock_discount_price &&
    productAmount?.storage_stock_price &&
    productAmount.storage_stock_discount_price <
      productAmount.storage_stock_price
  );

  const isAddToCartAllowed = useMemo(() => {
    if (!product) return false;

    const stockCount = productAmount?.storage_stock_count
      ? productAmount?.storage_stock_count
      : (productAmount?.wholesale_reserve?.amount_to ?? 0);
    const limitMin = productAmount?.storage_limit_min ?? 0;
    const step = productAmount?.step ?? 0;

    return (
      !product.hidden &&
      !!productAmount?.storage_unit &&
      stockCount > 0 &&
      stockCount >= limitMin &&
      stockCount >= step &&
      (!!Number(productAmount?.wholesale_reserve?.amount_to) ||
        stockCount > 0) &&
      (isEmarket ? product.emarket : product?.wholesale)
    );
  }, [product, productAmount, isEmarket]);

  const isAddToCartScrolledPast = useIsScrolledPast(
    addToCartRef,
    isAddToCartAllowed
  );

  const farmPath = isEmarket
    ? publicPath.farm(product?.farm?.slug, product?.farm_id)
    : publicPath.wholesaleFarm(product?.farm?.slug, product?.farm_id);

  const banner_3_data = banners.data.find(
    (banner) => banner.type === 'banner_3'
  );
  const banner_4_data = banners.data.find(
    (banner) => banner.type === 'banner_4'
  );

  const handleBack = () => {
    if (backTo) {
      navigate(backTo.path);
      return;
    }
    navigate(-1);
  };

  const addToCart = () => {
    if (!isEmarket && !hasWholesaleAccess) {
      setIsGateModalOpen(true);
      return;
    }

    cartDispatch({
      type: CartActionType.ADD_ITEM,
      product: product,
      amount: productAmount?.step ?? 1,
    });
  };

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <>
      <GlobalStyle />
      <StyledProductView
        className={`product-page${!isEmarket ? ' wholesale' : ''}`}
      >
        <section className={'product-intro'}>
          <div className={'container'}>
            <ProductBreadcrumbs
              crumbs={breadcrumbs}
              backTo={backTo}
              onBack={handleBack}
            />
            <ProductHero
              product={product}
              productAmount={productAmount}
              productImages={product?.images}
              qualityMarks={qualityMarks}
              isEmarket={isEmarket}
              amountCart={amountCart}
              isAddToCartAllowed={isAddToCartAllowed}
              addToCartRef={addToCartRef}
              onAddToCart={addToCart}
              onZoom={() => setIsGalleryOpen(true)}
            />
          </div>
        </section>

        <section className={'product-details'}>
          <div className={'container'}>
            <div className={'product-details-layout'}>
              <ProductTabs
                product={product}
                productAmount={productAmount}
                isEmarket={isEmarket}
                onGoToFarm={() => navigate(farmPath)}
              />
              <FarmLocationCard
                farm={product?.farm}
                infrastructures={product?.farm_infrastructures}
                producerName={product?.producer_name}
                isEmarket={isEmarket}
              />
            </div>
          </div>
        </section>

        <FarmProducts
          farmName={product?.farm?.brand ?? product?.farm?.name}
          products={otherProducts?.data}
          total={otherProducts?.total}
          pageSize={otherProducts?.per_page}
          currentPage={otherProducts?.current_page}
          onPageChange={(page) => fetchFarmProducts({ page, marketType })}
        />

        {(banner_3_data || banner_4_data) && (
          <div className={'banners'}>
            <div className={'container'}>
              {banner_3_data && (
                <Banner banners={[banner_3_data]} className={'first-banner'} />
              )}
              {banner_4_data && (
                <Banner banners={[banner_4_data]} className={'second-banner'} />
              )}
            </div>
          </div>
        )}

        {!isEmarket && (
          <WholesaleGateModal
            open={isGateModalOpen}
            onClose={() => setIsGateModalOpen(false)}
          />
        )}

        {isAddToCartAllowed && (
          <StickyAddToCart
            product={product}
            productAmount={productAmount}
            productHasDiscount={productHasDiscount}
            amountCart={amountCart}
            visible={isAddToCartScrolledPast}
            onAddToCart={addToCart}
          />
        )}

        {isGalleryOpen && (
          <div
            className={'product-lightbox'}
            onClick={() => setIsGalleryOpen(false)}
          >
            <div className={'product-lightbox-inner'} onClick={stopPropagation}>
              <button
                type={'button'}
                className={'product-lightbox-close'}
                onClick={() => setIsGalleryOpen(false)}
                aria-label={intl.formatMessage({ id: 'general.close' })}
              >
                <CloseOutlined />
              </button>
              <ProductGallery full productImages={product?.images} />
            </div>
          </div>
        )}
      </StyledProductView>
    </>
  );
};

export default ProductView;
